"use client";

import { useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useMutation } from "@tanstack/react-query";

import { completeOnboardingStep } from "@/lib/analytics/onboarding-funnel";
import { CpfStepForm } from "@/components/onboarding/cpf-step-form";
import { LoginFlow } from "@/components/onboarding/login-flow";
import { DashboardTopbar } from "@/components/shared/dashboard-topbar";
import { ScreenTransition } from "@/components/shared/screen-transition";
import { SignupFlow } from "@/components/onboarding/signup-flow";
import { clientPost } from "@/lib/http/client-http";
import { sanitizeCustomerRedirect } from "@/lib/auth/sanitize-redirect";
import { CPF_LOOKUP_ERROR, GENERIC_ERROR_MESSAGE } from "@/lib/errors/error-messages";
import { ONBOARDING_DESTINATION, type OnboardingIntent } from "@/lib/onboarding/onboarding-intent";
import { onboardingProgress } from "@/lib/onboarding/onboarding-progress";
import { ONBOARDING_VOICE } from "@/lib/onboarding/onboarding-voice";
import { StepLine } from "./step-line";
import type { SignupStep } from "./use-signup-wizard";

// ─── Types ───────────────────────────────────────────────────────────────────

type Flow = "cpf" | "login" | "signup";

type CpfLookupResponse =
  | { ok: true; exists: false }
  | { ok: true; exists: true; maskedEmail: string }
  | { ok: false };

/** The prototype's CNPJ guard (`ScreenCPF`), without the spaced dash the canon bans (§9). */
const CNPJ_MESSAGE = "Isso parece um CNPJ. A lista é criada com o seu CPF.";

// ─── Props ───────────────────────────────────────────────────────────────────

interface OnboardingFlowProps {
  /**
   * Which door this is: `/onboarding` is for someone who wants a list of their own,
   * `/onboarding/presentear` for someone paying for someone else's (`ONBOARDING_ENTRY`).
   */
  intent: OnboardingIntent;
  /** Skips CPF step, enters at the specified signup flow step. */
  initialStep?: SignupStep;
  /** CPF already validated by the session. */
  initialCpf?: string;
  /** Identity already REGISTERED — skip registration, collect remaining fields. */
  skipConfirmation?: boolean;
  /**
   * Whether a customer session exists. The header's "Sair" is only for someone signed in: the
   * prototype's onboarding header is the wordmark alone.
   */
  hasSession?: boolean;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function OnboardingFlow({ intent, initialStep, initialCpf = "", skipConfirmation = false, hasSession = false }: OnboardingFlowProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  // Where the person was going, and where this door leads when they were going nowhere in
  // particular: the checkout they left, or the panel.
  const customerRedirect = sanitizeCustomerRedirect(searchParams.get("next"), ONBOARDING_DESTINATION[intent]);

  const [flow, setFlow] = useState<Flow>(initialStep ? "signup" : "cpf");
  const [cpf, setCpf] = useState(initialCpf);
  const [maskedEmail, setMaskedEmail] = useState("");
  const [cpfError, setCpfError] = useState<string | null>(null);

  // Derived from props initially, but reset when user goes back to CPF and submits a new one.
  const [signupInitialStep, setSignupInitialStep] = useState<SignupStep | undefined>(initialStep);
  const [signupSkipConfirmation, setSignupSkipConfirmation] = useState(skipConfirmation);

  const cpfMutation = useMutation({
    mutationFn: (value: string) => clientPost<CpfLookupResponse>("/api/auth/onboarding/cpf", { cpf: value }),
    onSuccess: (data, value) => {
      if (!data.ok) {
        setCpfError(CPF_LOOKUP_ERROR);
        return;
      }
      setCpfError(null);
      setCpf(value);
      completeOnboardingStep("cpf");

      // Reset signup config — a fresh CPF lookup means start from scratch.
      setSignupInitialStep(undefined);
      setSignupSkipConfirmation(false);

      if (data.exists) {
        setMaskedEmail(data.maskedEmail);
        setFlow("login");
      } else {
        setFlow("signup");
      }
    },
    onError: () => setCpfError(GENERIC_ERROR_MESSAGE),
  });

  function renderFlow() {
    switch (flow) {
      case "cpf":
        return (
          <div className="max-w-[440px]">
            <StepLine {...onboardingProgress({ step: "cpf", intent })} />
            <CpfStepForm
              isPending={cpfMutation.isPending}
              errorMessage={cpfError}
              voice={ONBOARDING_VOICE[intent].cpf}
              onSubmit={(value) => cpfMutation.mutate(value)}
              // The host door's way out and its CNPJ sentence (the prototype's `ScreenCPF`). The guest
              // door has a bag waiting and no design for either.
              onExit={intent === "host" ? () => router.push("/") : undefined}
              cnpjMessage={intent === "host" ? CNPJ_MESSAGE : undefined}
            />
          </div>
        );

      case "login":
        return (
          <div className="max-w-[440px]">
            <StepLine {...onboardingProgress({ step: "login", intent })} />
            <LoginFlow
              maskedEmail={maskedEmail}
              redirectTo={customerRedirect}
              intent={intent}
              onBack={() => setFlow("cpf")}
            />
          </div>
        );

      case "signup":
        return (
          <SignupFlow
            cpf={cpf}
            intent={intent}
            redirectTo={customerRedirect}
            initialStep={signupInitialStep}
            skipConfirmation={signupSkipConfirmation}
            showIdentityEscape={!!signupInitialStep}
            onBack={() => setFlow("cpf")}
          />
        );
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-[var(--tb-white)]">
      <DashboardTopbar showSignOut={hasSession} />
      <div className="flex flex-1 items-center justify-center px-6 pb-24 pt-6 sm:px-12">
        <ScreenTransition screenKey={flow} animateExit skipFirstEntrance>
          {renderFlow()}
        </ScreenTransition>
      </div>
    </div>
  );
}
